<template>
  <hel-module title="采购核心指标" :class="scope.clsPrefix" v-loading="loading">
    <div class="overview-container">
      <div class="overview-main">
        <div
          class="overview-item"
          v-for="(item, index) in indicators"
          :key="item.name"
          @mouseenter="hoverIndex = index"
          @mouseleave="hoverIndex = -1"
        >
          <div class="item-header">
            <span class="item-name">{{ item.name }}</span>
            <span :class="['item-trend', item.trendClass]">
              <span class="trend-label">环比</span>
              <span class="trend-value">{{ item.trendValue }}</span>
            </span>
          </div>
          <div class="item-value">
            <span class="value-num">{{ item.value }}</span>
            <span class="value-unit">{{ item.unit }}</span>
          </div>
          <div class="item-mask" v-show="hoverIndex === index">
            <button class="smart-analysis-btn" @click.stop="handleSmartAnalysis(item)">
              智能分析
            </button>
          </div>
        </div>
      </div>
    </div>
  </hel-module>
</template>

<script>
import HelModule from '../common/module/index.vue'
import { getProcurementOverview } from '@/api/procurement'

// 采购核心指标定义：key / momKey 对应概览接口返回字段
// goodWhenUp：环比上升是否代表业务好转，决定颜色（与正负号无关）
// content：点击「智能分析」时向大模型发送的问题原文（保持原文，不做拼接）
const METRICS = [
  {
    name: '当月采购额',
    unit: '万',
    key: 'purchaseTotal',
    momKey: 'purchaseMomPct',
    fmt: 'number',
    goodWhenUp: false,
    content: '请分析「当月采购额」环比变动的构成原因，区分订单量变化与单价变化的影响，并按品类、供应商定位主要贡献来源。'
  },
  {
    name: '采购订单数',
    unit: '单',
    key: 'orderCount',
    momKey: 'orderMomPct',
    fmt: 'number',
    goodWhenUp: true,
    content: '请分析「采购订单数」环比大幅变动的结构原因，区分需求变化、集中合并下单与录单延迟三类情形，并列出需重点核对的订单。'
  },
  {
    name: '平均准时交货率',
    unit: '%',
    key: 'avgOnTimeRate',
    momKey: 'avgOnTimeMomPct',
    fmt: 'percent',
    goodWhenUp: true,
    content: '请分析「平均准时交货率」的供应商结构，识别影响交期的主要供应商与物料，区分改善贡献与拖累因素，并给出剩余风险点。'
  },
  {
    name: '在途订单数',
    unit: '单',
    key: 'inTransitOrderCount',
    momKey: 'inTransitOrderMomPct',
    fmt: 'number',
    goodWhenUp: false,
    content: '请分析「在途订单数」的交期分布，区分已逾期、7天内临期与正常在途三类，并给出跟催优先级与生产齐套影响评估。'
  },
  {
    name: '库存周转',
    unit: '天',
    key: 'inventoryTurnoverDays',
    momKey: 'inventoryTurnoverMomPct',
    fmt: 'number',
    goodWhenUp: false,
    content: '请分析「库存周转」天数变化的主要原因，按品类定位周转加快或变慢的来源，识别呆滞与超储物料，并给出管控方向。'
  },
  {
    name: '季度降本',
    unit: '万',
    key: 'quarterSavings',
    momKey: 'quarterSavingsMomPct',
    fmt: 'number',
    goodWhenUp: true,
    content: '请分析「本季度降本」的完成情况，并给出采购优化方案'
  }
]

export default {
  name: 'ProcurementOverview',
  components: {
    HelModule
  },
  props: {
    data: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  data() {
    return {
      loading: false,
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-procurement-overview'
      },
      hoverIndex: -1,
      // 初始占位：接口未返回或字段缺失时统一显示 --
      indicators: METRICS.map((metric) => ({
        name: metric.name,
        unit: metric.unit,
        content: metric.content,
        value: '--',
        trendValue: '--',
        trendClass: 'trend-flat'
      }))
    }
  },
  created() {
    this.fetchCoreMetrics()
  },
  methods: {
    handleSmartAnalysis(item) {
      // 携带该指标的问题原文，宿主 handleSmartAnalysis 按 type=procurementKpi 原文直发
      this.$emit('smart-analysis', { ...item, type: 'procurementKpi' })
    },
    isBlank(value) {
      return value === null || value === undefined || value === '' || isNaN(Number(value))
    },
    formatNumber(value) {
      if (this.isBlank(value)) return '--'
      return Number(value).toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    },
    formatPercent(value) {
      if (this.isBlank(value)) return '--'
      return Number(value).toFixed(1)
    },
    // 环比文案为「±x.x%」；颜色按业务语义：好转为绿、恶化为红，与正负号无关
    buildTrend(metric, momValue) {
      if (this.isBlank(momValue)) {
        return { trendValue: '--', trendClass: 'trend-flat' }
      }
      const num = Number(momValue)
      const trendValue = (num > 0 ? '+' : '') + num.toFixed(1) + '%'
      if (num === 0) {
        return { trendValue, trendClass: 'trend-flat' }
      }
      const isGood = num > 0 ? metric.goodWhenUp : !metric.goodWhenUp
      return { trendValue, trendClass: isGood ? 'trend-good' : 'trend-bad' }
    },
    async fetchCoreMetrics() {
      this.loading = true
      try {
        const res = await getProcurementOverview()
        if (!res || res.code !== 200 || !res.data) {
          return
        }
        const m = res.data
        this.indicators = METRICS.map((metric) => {
          const trend = this.buildTrend(metric, m[metric.momKey])
          return {
            name: metric.name,
            unit: metric.unit,
            content: metric.content,
            value: metric.fmt === 'percent' ? this.formatPercent(m[metric.key]) : this.formatNumber(m[metric.key]),
            trendValue: trend.trendValue,
            trendClass: trend.trendClass
          }
        })
      } catch (error) {
        console.error('获取采购指标失败:', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$class-prefix: "rvc-cosmoplat-workspace-procurement-overview";
.overview-main {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
}
.#{$class-prefix} {
  overflow: hidden;
  .overview-container {
    height: 100%;
    width: 100%;
    // overflow-x: auto;
    scrollbar-width: thin;
    scrollbar-color: transparent;
  }
  .overview-container:hover {
    scrollbar-width: thin;
    scrollbar-color: #b0b0b0 #e8e8e8;
  }
  .overview-item {
    min-width: 120px;
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    background: #eff2fc;
    padding: 10px 12px;
    position: relative;
    .item-header {
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin-bottom: 6px;
    }
    .item-name {
      font-size: 12px;
      font-family: PingFangSC-Medium;
      font-weight: 600;
      color: #333333;
      white-space: nowrap;
    }
    .item-trend {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      font-family: PingFangSC-Regular;
      font-weight: 400;
      white-space: nowrap;
      .trend-label {
        color: #909399;
      }
      // 好转
      &.trend-good {
        .trend-value {
          color: #73b950;
        }
      }
      // 恶化
      &.trend-bad {
        .trend-value {
          color: #f44f4f;
        }
      }
      // 持平 / 无数据
      &.trend-flat {
        .trend-value {
          color: #909399;
        }
      }
    }
    .trend-value {
      font-weight: 500;
    }
    .item-value {
      display: flex;
      align-items: baseline;
      gap: 6px;
    }
    .value-num {
      font-size: 24px;
      font-weight: 700;
      font-family: PingFangSC-Semibold;
      color: #333;
      line-height: 32px;
    }
    .value-unit {
      font-size: 12px;
      color: #333;
      font-weight: 400;
    }
    .item-mask {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: #edf1fb3b;
      backdrop-filter: blur(1px);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .smart-analysis-btn {
      width: 64px;
      height: 24px;
      border: none;
      background-image: linear-gradient(270deg, #4a5ff3 0%, #4e86ff 100%);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-family: PingFangSC-Regular;
      font-weight: 400;
      font-size: 12px;
      color: #ffffff;
      line-height: 12px;
    }
  }
}
@media screen and (max-width: 1440px) {
  .#{$class-prefix} {
    .overview-item {
      padding: 8px 10px;
      .value-num {
        font-size: 18px;
      }
      .item-name {
        font-size: 11px;
      }
      .item-trend {
        font-size: 11px;
      }
    }
  }
}
</style>

<style lang="scss">
.rvc-cosmoplat-workspace-procurement-overview .overview-container::-webkit-scrollbar {
  height: 0;
}
.rvc-cosmoplat-workspace-procurement-overview
  .overview-container:hover::-webkit-scrollbar {
  height: 6px;
}
.rvc-cosmoplat-workspace-procurement-overview
  .overview-container::-webkit-scrollbar-track {
  background: #e8e8e8;
  border-radius: 3px;
}
.rvc-cosmoplat-workspace-procurement-overview
  .overview-container::-webkit-scrollbar-thumb {
  background: #b0b0b0;
  border-radius: 3px;
}
.rvc-cosmoplat-workspace-procurement-overview
  .overview-container::-webkit-scrollbar-thumb:hover {
  background: #909090;
}
</style>
